<template>
  <div class="type-box-root" style="width: inherit;">
    <div class="type-box-div input-field" style="position: relative; display: inline-block; width: 100%">
      <textarea rows="3" class="chat-type-box materialize-textarea" ref="textarea"
          :placeholder="placeholder"
          @keydown.enter.exact.stop.prevent="sendChatMessage"
          @keydown.arrow-up.exact.stop.prevent="onArrowUp"
          @keydown="onInput"
          :disabled="!hasChatPermission"
          v-model="typedMessage">
      </textarea>
      <EmojiPicker @emoji="insertEmoji" :search="emojiSearch" :emoji-table="emojiTable" ref="emojiPicker">
        <div slot="emoji-invoker" slot-scope="{ events: { click: clickEvent } }" @click.stop="clickEvent">
          <a class="emoji-invoker">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 0h24v24H0z" fill="none"/>
              <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
            </svg>
          </a>
        </div>
        <div slot="emoji-picker" slot-scope="{ emojis, insert }">
          <transition name="fade" appear>
            <div class="emoji-picker scroll">
              <div class="emoji-picker__search">
                <input type="text" class="browser-default" v-model="emojiSearch" @focus="emojiSearchFocused = true">
              </div>
              <div>
                <div v-for="(emojiGroup, category) in emojis" :key="category">
                  <h5>{{ category }}</h5>
                  <div class="emojis">
                    <span
                      v-for="(emoji, emojiName) in emojiGroup"
                      :key="emojiName"
                      @click="insert({ emoji, name: emojiName })"
                      :title="emojiName"
                      class="emoji"
                    >{{ emoji }}</span>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </EmojiPicker>
    </div>
    <div class="is-typing is-clipped" style="width: inherit;">
      <div v-if="typingUsersArray.length > 0" class="is-inline-flex" style="width: inherit; height: inherit;">
        <PulsatingEllipses :dot-size="1.6" color="#ababab" style="white-space: nowrap;" class="ellipses"/>
        <div class="users" style="margin-right: 4px"> {{ typingUsersStr }} </div>
        <div> {{ typingSuffix }} </div>
      </div>
    </div>
    <!-- <label for="type-box">Send message to all participants in room</label> -->
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import AppBus from '@C/bus/app-bus.vue'
import { RoomEventTypes, RoomChatTypes } from '@root/backend/databases/events'
import throttle from 'lodash/throttle'
import DefaultEmojiTable from 'vue-emoji-picker/src/emojis'
import Graphemer from 'graphemer'

const whitespacePattern = /[ \t\n]+/
const emojiPattern = /\p{Extended_Pictographic}/u

export default {
  name: 'type-box',
  components: {
    PulsatingEllipses: () => import(/* webpackChunkName: "pulsating-ellipses" */ '@C/common/pulsating-ellipses.vue'),
    EmojiPicker: () => import(/* webpackChunkName: "emoji-picker" */ 'vue-emoji-picker')
  },
  props: {
    showButton: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      emojiSearch: '',
      cache: undefined,
      emojiTable: undefined,
      emojiSearchFocused: false
    }
  },
  computed: {
    ...mapGetters([
      'socket',
      'roomName',
      'roomProperties',
      'roomOwner',
      'roomController',
      'userProfile',
      'screenType',
      'typingUsers',
      'showingDiscussion',
      'recentlyUsedEmojis',
      'typedEmojis'
    ]),
    placeholder () {
      const { hasChatPermission, roomProperties = {} } = this
      const { textChatType } = roomProperties
      if (textChatType === RoomChatTypes.NO_CHAT) {
        return 'Chat has been disabled in this room'
      } else if (textChatType === RoomChatTypes.MOD_CHAT && !hasChatPermission) {
        return 'Only moderators can send chat messages'
      }
      return 'Send a message'
    },
    hasChatPermission () {
      const { roomOwner, roomController, roomProperties = {} } = this
      const { textChatType } = roomProperties
      return textChatType === RoomChatTypes.ALL_CHAT || (textChatType === RoomChatTypes.MOD_CHAT && (roomOwner || roomController))
    },
    typedMessage: {
      get () {
        return this.$store.getters.typedMessage
      },
      set (v) {
        this.$store.commit('typedMessage', v)
      }
    },
    typingUsersArray () {
      return Object.values(this.typingUsers)
    },
    typingUsersStr () {
      const { typingUsersArray } = this
      const numTypingUsers = typingUsersArray.length
      if (numTypingUsers > 3) {
        return (`${numTypingUsers} users`)
      }
      const truncatedUsers = typingUsersArray.map(user => {
        if (user.length > 8) {
          return `${user.substring(0, 8)}...`
        }
        return user
      })
      const truncatedResult = `${truncatedUsers.join(', ')}`
      const result = `${typingUsersArray.join(', ')}`
      if (result.length > 40) {
        return truncatedResult
      }
      return result
    },
    typingSuffix () {
      const { typingUsersArray } = this
      const numTypingUsers = typingUsersArray.length
      return `${numTypingUsers > 1 ? 'are' : 'is'} typing`
    }
  },
  watch: {
    typedMessage: throttle(function (v, o) {
      const trimmed = v.trim()
      if (trimmed.length > 0) {
        this.socket.emit('user-typing')
      } else if (trimmed.length === 0 && o.length > 0) {
        // Backspaced to empty
        this.socket.emit('user-typing', { cancel: true })
      }
    }, 300),
    recentlyUsedEmojis (v) {
      this.cache.load(v)
      this.updateEmojiTable()
    }
  },
  methods: {
    async sendChatMessage () {
      const { roomName, cache, userProfile: { nickname, userHash } } = this
      let { typedEmojis } = this
      var msg = this.typedMessage.trim()
      if (msg === '') {
        return
      }
      var json = {
        msg,
        roomName,
        nickname,
        userHash
      }
      this.socket.emit(RoomEventTypes.ROOM_CHAT_MESSAGE, json)

      // Ensure that we're only updating recently used emojis with ones that were actually sent out
      const graphemes = new Graphemer().splitGraphemes(this.typedMessage)
      const emojis = new Set(graphemes.filter(x => emojiPattern.test(x)))
      typedEmojis = typedEmojis.filter(x => emojis.has(x.emoji))

      this.typedMessage = ''
      window.M.textareaAutoResize(this.$refs.textarea)
      if (!cache) {
        return
      }
      for (const entry of typedEmojis) {
        const { name, emoji } = entry
        this.cache.set(name, emoji)
      }
      this.$store.commit('typedEmojis', [])
      if (typedEmojis.length > 0) {
        this.$store.commit('recentlyUsedEmojis', this.cache.dump())
      }
    },
    async onArrowUp () {
      const msg = this.typedMessage.trim()
      if (msg.length !== 0) {
        return
      }
      AppBus.$once('edit-complete', () => {
        this.$refs.textarea.focus()
      })
      AppBus.$emit('edit-last-message')
    },
    async onInput (e) {
      // If the keypress is (CTRL|META) + (.|;), then bring up the emoji picker
      if ((e.metaKey || e.ctrlKey) && (e.key === '.' || e.key === ';')) {
        e.stopImmediatePropagation()
        e.preventDefault()
        this.$el.querySelector('.emoji-invoker').click()
      }
    },
    insertEmoji ({ emoji, name }) {
      const additions = [emoji]
      const graphemes = new Graphemer().splitGraphemes(this.typedMessage)
      const lastGrapheme = graphemes.slice(-1)[0]
      if (!whitespacePattern.test(lastGrapheme) && !emojiPattern.test(lastGrapheme)) {
        // Add a space if the last character was not whitespace and not an emoji
        additions.unshift(' ')
      }
      this.typedMessage = this.typedMessage + additions.join('')
      this.typedEmojis.push({ emoji, name })
    },
    updateEmojiTable () {
      const recentlyUsedEmojis = {}
      delete DefaultEmojiTable['Frequently used']
      const result = {
        'Recently used': recentlyUsedEmojis,
        ...DefaultEmojiTable
      }
      // Update recently used from cache
      const { cache } = this
      if (cache) {
        [...cache.keys()].forEach(key => {
          recentlyUsedEmojis[key] = cache.get(key)
        })
      }
      if (Object.keys(recentlyUsedEmojis).length === 0) {
        // Remove the key
        delete result['Recently used']
      }
      this.emojiTable = result
    },
    focusEmojis () {
      this.$refs.emojiPicker.$el.querySelector('.emoji').focus()
    }
  },
  async beforeMount () {
    const { default: LRUCache } = await import(/* webpackChunkName: "lru-cache" */ 'lru-cache')
    const cache = new LRUCache({
      max: 10
    })
    try {
      cache.load(this.$store.getters.recentlyUsedEmojis)
    } catch (e) {
      this.$store.commit('recentlyUsedEmojis', cache.dump())
    }
    this.cache = cache
    this.updateEmojiTable()

    this.$on('focus-input', e => {
      const emojiPickerInput = this.$el.querySelector('.emoji-picker__search > input')
      const textarea = this.$el.querySelector('.chat-type-box')
      const isEnterKey = e.key === 'Enter'

      let element = emojiPickerInput || textarea
      if (isEnterKey) {
        if (!e.shiftKey) {
          // This is an enter key without shift. We're going to treat this as a send
          this.$refs.emojiPicker.display.visible = false
          return this.sendChatMessage()
        } else {
          // This is a shift+enter. This should be sent to the textarea
          element = textarea
        }
      } else if (e.key === ' ') {
        element = textarea
      }
      element.focus()
      if (e.key.length > 1) {
        // We only handle one character keys
        switch (e.keyCode) {
          case 8: // Backspace
            // visibleTypeBox.value = visibleTypeBox.value.slice(0, visibleTypeBox.value.length - 1)
            break
          case 13: // Enter
          case 37: // Left
          case 38: // Up
          case 39: // Right
          case 40: // Down
            // const evt = new KeyboardEvent('keydown', e)
            // visibleTypeBox.dispatchEvent(evt)
            break
        }
      } else {
        // visibleTypeBox.value += e.key
      }
    })
  },
  mounted: function () {
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/transitions.scss';

$textarea-height: 32px;

$emoji-invoker-margin-top: 4px;
$emoji-invoker-margin-right: 8px; // This should equal whatever the scrollbar's width is
$emoji-invoker-button-size: calc(#{$textarea-height} - (#{$emoji-invoker-margin-top} * 2));
$emoji-picker-border-radius: 4px;

.dark, .theater-mode {
  .type-box-div {
    textarea {
      background: $chat-dark-bg;
      color: $chat-dark-text;
    }
    .chat-type-box {
      border-top-color: var(--background-lighten-20);
    }
  }
  .is-typing {
    .ellipses {
      ::v-deep svg {
        circle {
          stroke: #ababab !important;
          fill: #ababab !important;
        }
      }
    }
  }
}

@include until($desktop) {
  .type-box-div {
    margin-top: 2%;
  }
}

.chat-type-box {
  background-color: white;
  width: 100%;
  padding: 0.4em 6px 0 4px;
  margin: 0;
  border-top-color: var(--background-darken-10);
  border-top-style: solid;
  border-top-width: 1px;
  max-height: 5em;
  height: 1em;
  overflow-y: auto;
  min-height: 2em !important;
  &[disabled] {
    cursor: not-allowed;
  }
}

.type-box-div {
  margin-top: 0;
  margin-bottom: 0;
  > textarea {
    background: var(--background-darken-5);
    color: $chat-light-text;
    height: $textarea-height;
    padding-right: calc(#{$emoji-invoker-button-size} + #{$emoji-invoker-margin-right});
    overflow-y: scroll; // Always show scrollbar so that emoji picker doesn't move
    @include style-scroll;
  }
  .send-btn {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    padding-left: 4px;
  }
}

.is-typing {
  .ellipses {
    ::v-deep svg {
      circle {
        stroke: #6a6a6a !important;
        fill: #6a6a6a !important;
      }
    }
    margin-right: 6px;
  }
  height: 18px;
  font-size: 0.8em;
  .users {
    max-width: calc(100% - (100px));
    text-overflow: ellipsis;
    word-break: break-all;
    white-space: nowrap;
    overflow: hidden;
  }
}

// EmojiPicker CSS
::v-deep .emoji-invoker {
  position: absolute;
  top: $emoji-invoker-margin-top;
  right: $emoji-invoker-margin-right;
  width: $emoji-invoker-button-size;
  height: $emoji-invoker-button-size;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
}
.emoji-invoker:hover {
  transform: scale(1.1);
}
.emoji-invoker > svg {
  fill: #b1c6d0;
  width: $emoji-invoker-button-size;
  height: $emoji-invoker-button-size;
}

::v-deep .emoji-picker {
  position: absolute;
  z-index: 1;
  width: 100%;
  bottom: calc(#{$textarea-height} + #{$emoji-picker-border-radius} + 3px);
  height: 20rem;
  overflow: auto;
  padding: 1rem;
  box-sizing: border-box;
  border-radius: $emoji-picker-border-radius;
  background: var(--background-lighten-10);
}
.emoji-picker__search {
  display: flex;
}
.emoji-picker__search > input {
  width: 100%;
  flex: 1;
  border-radius: 10rem;
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  outline: none;
  margin-bottom: 0.3rem;
  &:focus {
    box-shadow: none !important;
    border: 1px solid var(--primary-color-lighten-10) !important
  }
}
.emoji-picker h5 {
  margin-bottom: 0;
  color: #b1b1b1;
  text-transform: uppercase;
  font-size: 0.8rem;
  cursor: default;
}
.emoji-picker .emojis {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}
.emoji-picker .emojis:after {
  content: "";
  flex: auto;
}
.emoji-picker .emojis span {
  padding: 0.2rem;
  cursor: pointer;
  border-radius: 5px;
}
.emoji-picker .emojis span:hover {
  background: var(--primary-color-lighten-10);
  cursor: pointer;
}

</style>
